// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { createOfflineFetch, offlineLayerEnabled } from '@/lib/offline/offlineFetch';
import { startNetMonitor } from '@/lib/offline/net';
import { platform } from '@/platform/runtime';
import { PRODUCTS, productForPath } from '@/platform/products';
import { BANK_LINK_RETURN_KEY } from '@/lib/open-banking';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

/**
 * FIX-04 — Module-aware localStorage adapter.
 *
 * Without this fix, all modules share one Supabase auth storage key (e.g.
 * "sb-{ref}-auth-token"). When a user logs into Core admin in Tab 1 and a
 * tenant user logs into Construction in Tab 2, the second login silently
 * overwrites the first, corrupting both sessions.
 *
 * This adapter reads window.location.pathname to derive the active module and
 * prefixes every storage key accordingly:
 *   /core/...         → "core-sb-..."
 *   /construction/... → "construction-sb-..."
 *   /billing/...      → "billing-sb-..."
 *   /crm/...          → "crm-sb-..."
 *
 * Result: each module has a completely isolated auth token slot in localStorage.
 * No other files need to change — one shared client instance, correct isolation.
 */
/**
 * Pagine condivise visitate durante un flusso partito da un prodotto (es. /auth/banca al ritorno
 * dalla banca): usano la sessione del prodotto di partenza, salvata prima di uscire dall'app.
 */
function returnPathOwner() {
  try {
    const from = sessionStorage.getItem(BANK_LINK_RETURN_KEY);
    return from ? productForPath(from) : null;
  } catch {
    return null;
  }
}

function getModulePrefix(): string {
  if (typeof window === 'undefined') return 'default';
  // Dominio dedicato a un prodotto (o app nativa): una sola sessione, legata al prodotto e non
  // al percorso — così resta valida anche quando gli URL perderanno il prefisso /construction.
  // Se l'host serve più prodotti (Console + prodotti senza dominio) resta la regola per percorso.
  if (platform.product && platform.mounted.length === 1) return PRODUCTS[platform.product].sessionKey;
  // Regola per percorso, dal registro prodotti: ogni pagina usa lo slot del prodotto che la possiede
  // (/settings e /platform-admin sono Console → "core"; prima finivano in "default" e chiedevano
  // un nuovo accesso). Pagine condivise (/onboarding, /auth, /privacy) → "default", come prima.
  const owner = productForPath(window.location.pathname) ?? returnPathOwner();
  return owner ? PRODUCTS[owner].sessionKey : 'default';
}

const moduleAwareStorage = {
  getItem(key: string): string | null {
    return localStorage.getItem(`${getModulePrefix()}-${key}`);
  },
  setItem(key: string, value: string): void {
    localStorage.setItem(`${getModulePrefix()}-${key}`, value);
  },
  removeItem(key: string): void {
    localStorage.removeItem(`${getModulePrefix()}-${key}`);
  },
};

/**
 * Livello offline-first (solo app nativa o flag di collaudo): letture dalla cache locale quando manca la rete,
 * scritture operative in coda e reinviate al ritorno della rete. Sul web normale non cambia nulla.
 */
const OFFLINE_LAYER = offlineLayerEnabled();

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: moduleAwareStorage,
    persistSession: true,
    autoRefreshToken: true,
  },
  ...(OFFLINE_LAYER ? { global: { fetch: createOfflineFetch({ backendUrl: SUPABASE_URL }) } } : {}),
});

if (OFFLINE_LAYER) {
  // App nativa: gli errori delle funzioni server mostrano il messaggio vero (non «non-2xx status code»)
  const originalInvoke = supabase.functions.invoke.bind(supabase.functions);
  supabase.functions.invoke = (async (...args: Parameters<typeof originalInvoke>) => {
    const res = await originalInvoke(...args);
    if (res.error) {
      try {
        const { functionErrorMessage } = await import('@/lib/functionError');
        const msg = await functionErrorMessage(res.error, res.error.message);
        if (msg && msg !== res.error.message) res.error.message = msg;
      } catch {
        /* resta il messaggio originale */
      }
    }
    return res;
  }) as typeof supabase.functions.invoke;
}

if (OFFLINE_LAYER) {
  startNetMonitor({ backendUrl: SUPABASE_URL, apiKey: SUPABASE_PUBLISHABLE_KEY });
}